import React from 'react';
import { X, Scan, Plus, Minus } from 'lucide-react';

// Fallback defaults – overridden by props when available
// Fallback defaults – overridden by props when available
const DEFAULT_CANVAS_W = 2000;
const DEFAULT_CANVAS_H = 1400;

/* ═══════════════════════════════════════════════
 *  MiniMap – Graph mini navigation map
 * ═══════════════════════════════════════════════ */
class MiniMap extends React.Component {
    constructor(props) {
        super(props);
        this.state = { isDragging: false };
        this.containerRef = React.createRef();
    }

    handleMouseDown = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.setState({ isDragging: true });
        this.updateScrollFromEvent(e);
        window.addEventListener('mousemove', this.handleMouseMove);
        window.addEventListener('mouseup', this.handleMouseUp);
    };

    handleMouseMove = (e) => {
        if (!this.state.isDragging) return;
        this.updateScrollFromEvent(e);
    };

    handleMouseUp = () => {
        this.setState({ isDragging: false });
        window.removeEventListener('mousemove', this.handleMouseMove);
        window.removeEventListener('mouseup', this.handleMouseUp);
    };

    updateScrollFromEvent = (e) => {
        const el = this.containerRef.current;
        if (!el || !this.props.onPanTo) return;
        const rect = el.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const clickY = e.clientY - rect.top;
        const pctX = Math.max(0, Math.min(1, clickX / rect.width));
        const pctY = Math.max(0, Math.min(1, clickY / rect.height));
        this.props.onPanTo(pctX, pctY);
    };

    render() {
        const {
            cards = [],
            posMap = {},
            canvasW,
            canvasH,
            scrollLeft,
            scrollTop,
            stageW,
            stageH,
            zoom,
            marginLeft = 0,
            marginTop = 0,
            focusId = null
        } = this.props;

        // Still render chrome even before stage measures, so thumbnail is visible
        const safeStageW = stageW || 1;
        const safeStageH = stageH || 1;
        if (!canvasW || !canvasH) return null;

        const MINI_W = 200;
        const MINI_H = 125;
        const PADDING_X = 14;
        const PADDING_Y = 12;

        const availW = MINI_W - PADDING_X * 2;
        const availH = MINI_H - PADDING_Y * 2;

        // Fit to card bounds (not full empty canvas) so mini boxes stay visible
        let minX = 0;
        let maxX = canvasW;
        let minY = 0;
        let maxY = canvasH;

        if (cards.length > 0) {
            let cMinX = Infinity;
            let cMaxX = -Infinity;
            let cMinY = Infinity;
            let cMaxY = -Infinity;
            let validCardsCount = 0;

            cards.forEach((card) => {
                const p = posMap[card.id];
                if (p) {
                    validCardsCount++;
                    if (p.x < cMinX) cMinX = p.x;
                    if (p.x + p.w > cMaxX) cMaxX = p.x + p.w;
                    if (p.y < cMinY) cMinY = p.y;
                    if (p.y + p.h > cMaxY) cMaxY = p.y + p.h;
                }
            });

            if (validCardsCount > 0) {
                const pad = 40;
                minX = cMinX - pad;
                maxX = cMaxX + pad;
                minY = cMinY - pad;
                maxY = cMaxY + pad;
            }
        }

        const graphW = Math.max(100, maxX - minX);
        const graphH = Math.max(100, maxY - minY);

        const scaleX = availW / graphW;
        const scaleY = availH / graphH;
        const scale = Math.min(scaleX, scaleY);

        const contentW = graphW * scale;
        const contentH = graphH * scale;

        const offsetX = (MINI_W - contentW) / 2;
        const offsetY = (MINI_H - contentH) / 2;

        const validZoom = isNaN(zoom) || zoom <= 0 ? 1.0 : zoom;
        const totalScaledW = canvasW * validZoom;
        const totalScaledH = canvasH * validZoom;

        const visibleRatioW = Math.min(1, safeStageW / Math.max(1, totalScaledW));
        const visibleRatioH = Math.min(1, safeStageH / Math.max(1, totalScaledH));

        const mapCanvasW = graphW * scale;
        const mapCanvasH = graphH * scale;

        const VP_PAD = 3;
        const rawVpW = Math.max(18, mapCanvasW * visibleRatioW);
        const rawVpH = Math.max(14, mapCanvasH * visibleRatioH);

        const maxScrollX = Math.max(1, (marginLeft + totalScaledW + marginLeft) - safeStageW);
        const maxScrollY = Math.max(1, (marginTop + totalScaledH + marginTop) - safeStageH);

        const scrollRatioX = Math.max(0, Math.min(1, scrollLeft / maxScrollX));
        const scrollRatioY = Math.max(0, Math.min(1, scrollTop / maxScrollY));

        const rawVpX = offsetX + Math.max(0, Math.min(mapCanvasW - rawVpW, scrollRatioX * (mapCanvasW - rawVpW)));
        const rawVpY = offsetY + Math.max(0, Math.min(mapCanvasH - rawVpH, scrollRatioY * (mapCanvasH - rawVpH)));

        const vpX = Math.max(2, rawVpX - VP_PAD);
        const vpY = Math.max(2, rawVpY - VP_PAD);
        const vpW = Math.min(MINI_W - vpX - 2, rawVpW + VP_PAD * 2);
        const vpH = Math.min(MINI_H - vpY - 2, rawVpH + VP_PAD * 2);

        const miniNodes = [];
        cards.forEach((card) => {
            const p = posMap[card.id];
            if (!p) return;

            const baseNx = offsetX + (p.x - minX) * scale;
            const baseNy = offsetY + (p.y - minY) * scale;
            const baseNw = Math.max(10, p.w * scale);
            const baseNh = Math.max(8, p.h * scale);

            miniNodes.push({
                card,
                nx: baseNx,
                ny: baseNy,
                nw: baseNw,
                nh: baseNh,
                isFocus: focusId != null && String(card.id) === String(focusId)
            });
        });

        return (
            <div
                className="qd-data-lineage-minimap lin-minimap"
                ref={this.containerRef}
                onMouseDown={this.handleMouseDown}
                title="MiniMap – Click or drag to navigate graph"
            >
                <div
                    className="qd-data-lineage-minimap__inner lin-minimap-inner"
                    style={{ width: `${MINI_W}px`, height: `${MINI_H}px` }}
                >
                    {miniNodes.map(({ card, nx, ny, nw, nh, isFocus }) => {
                        const isCritical = card.status === 'critical' || card.isCritical;
                        const isSource = card.type === 'source' || card.isSource;
                        let color = '#94A3B8';
                        let border = '1px solid transparent';
                        if (isCritical) {
                            color = '#F5476B';
                            border = '1px solid #F5476B';
                        } else if (isFocus) {
                            color = '#6366F1';
                            border = '1px solid #4338ca';
                        } else if (isSource) {
                            color = '#007BFF';
                        }

                        return (
                            <div
                                key={card.id}
                                className={`qd-data-lineage-minimap__node lin-mini-node${isFocus ? ' is-focus' : ''}${isCritical ? ' is-critical' : ''}${isSource ? ' is-source' : ''}`}
                                style={{
                                    left: `${Math.round(nx * 10) / 10}px`,
                                    top: `${Math.round(ny * 10) / 10}px`,
                                    width: `${Math.round(nw * 10) / 10}px`,
                                    height: `${Math.round(nh * 10) / 10}px`,
                                    backgroundColor: color,
                                    border,
                                    boxSizing: 'border-box'
                                }}
                            />
                        );
                    })}

                    <div
                        className="qd-data-lineage-minimap__vp lin-minimap-vp"
                        style={{
                            left: `${vpX}px`,
                            top: `${vpY}px`,
                            width: `${vpW}px`,
                            height: `${vpH}px`
                        }}
                    />
                </div>
            </div>
        );
    }
}

/**
 * Shared pan/zoom/theme chrome for LineageModal and WorkflowModal.
 * Pass header + canvas body via props; shell owns interaction state.
 *
 * New props:
 *  - canvasW / canvasH: dynamic canvas dimensions
 *  - autoFit: if true, automatically fit-to-screen on first render
 */
export default class GraphCanvasShell extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            zoom: 1.0,
            marginLeft: 24,
            marginTop: 24,
            marginRight: 24,
            marginBottom: 24,
            scrollLeft: 0,
            scrollTop: 0,
            stageW: 0,
            stageH: 0,
            ready: false,
            theme: props.defaultTheme || 'light'
        };
        this.stageRef = React.createRef();
        this.panActive = false;
        this.panStartX = 0;
        this.panStartY = 0;
        this.panStartScrollLeft = 0;
        this.panStartScrollTop = 0;
        this.resizeObserver = null;
        this.resizeRaf = null;
        this.scrollRaf = null;
        this.didAutoFit = false;
    }

    get cw() { return this.props.canvasW || DEFAULT_CANVAS_W; }
    get ch() { return this.props.canvasH || DEFAULT_CANVAS_H; }

    componentDidMount() {
        if (this.props.show) {
            this.openShell();
        }
    }

    componentDidUpdate(prevProps) {
        if (prevProps.show !== this.props.show) {
            if (this.props.show) {
                this.didAutoFit = false;
                this.openShell();
            } else {
                this.closeShell();
            }
            return;
        }

        // NB: `children` is a fresh element on every parent render, so comparing it
        // by identity matched constantly and re-centered the scroll on any state
        // change (expanding a card, tracing, opening the panel). Only a real content
        // swap re-centers; a size change just refreshes the centering margins.
        const contentChanged = prevProps.contentKey !== this.props.contentKey;
        const sizeChanged = prevProps.canvasW !== this.props.canvasW
            || prevProps.canvasH !== this.props.canvasH;

        if (this.props.show && (contentChanged || sizeChanged)) {
            if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);
            this.resizeRaf = requestAnimationFrame(() => {
                this.updateLayout({ centerScroll: contentChanged });
                if (!this.state.ready) this.setState({ ready: true });
                if (contentChanged) {
                    this.centerOnFocus(true);
                }
            });
        }

        if (this.props.show && prevProps.focusId !== this.props.focusId && this.props.focusId) {
            this.centerOnFocus();
        }
    }

    componentWillUnmount() {
        this.closeShell();
        window.removeEventListener('mousemove', this.handleMouseMove);
        window.removeEventListener('mouseup', this.handleMouseUp);
        if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);
    }

    getPanPad = (stage) => {
        if (!this.props.embedded) return 24;
        const h = stage?.clientHeight || 0;
        const w = stage?.clientWidth || 0;
        return Math.max(120, Math.round(h * 0.18), Math.round(w * 0.08));
    };

    openShell = () => {
        if (!this.props.embedded) {
            document.body.style.overflow = 'hidden';
        }
        this.observeStage();
        if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);
        this.resizeRaf = requestAnimationFrame(() => {
            const stage = this.stageRef.current;
            let nextZoom = this.state.zoom;
            if (this.props.autoFit && !this.didAutoFit && stage && stage.clientWidth > 0 && stage.clientHeight > 0) {
                this.didAutoFit = true;
                const fitW = (stage.clientWidth - 120) / this.cw;
                const fitH = (stage.clientHeight - 120) / this.ch;
                nextZoom = Math.min(1.0, Math.max(0.2, Math.min(fitW, fitH)));
            }
            const panPad = this.getPanPad(stage);
            const visualW = this.cw * nextZoom;
            const visualH = this.ch * nextZoom;
            let marginLeft = panPad;
            let marginTop = panPad;
            if (!this.props.embedded && stage && stage.clientWidth > 0) {
                marginLeft = Math.max(panPad, Math.round((stage.clientWidth - visualW) / 2));
            }
            if (!this.props.embedded && stage && stage.clientHeight > 0) {
                marginTop = Math.max(panPad, Math.round((stage.clientHeight - visualH) / 2));
            }

            this.setState({
                zoom: nextZoom,
                marginLeft,
                marginTop,
                marginRight: panPad,
                marginBottom: panPad,
                ready: true,
                stageW: stage ? stage.clientWidth : 0,
                stageH: stage ? stage.clientHeight : 0
            }, () => {
                if (this.props.focusId) {
                    this.centerOnFocus(true);
                } else if (stage) {
                    stage.scrollLeft = Math.max(0, (stage.scrollWidth - stage.clientWidth) / 2);
                    stage.scrollTop = Math.max(0, (stage.scrollHeight - stage.clientHeight) / 2);
                }
            });
        });
    };

    centerOnFocus = (force = false) => {
        const stage = this.stageRef.current;
        const { focusId, posMap = {} } = this.props;
        if (!stage || !focusId) return;
        const rect = posMap[focusId];
        if (!rect) return;

        const { zoom, marginLeft = 0, marginTop = 0 } = this.state;
        const validZoom = isNaN(zoom) || zoom <= 0 ? 1.0 : zoom;
        const centerX = marginLeft + ((rect.x + (rect.w || 0) / 2) * validZoom);
        const centerY = marginTop + ((rect.y + (rect.h || 0) / 2) * validZoom);

        const nextLeft = Math.max(0, centerX - (stage.clientWidth / 2));
        const nextTop = Math.max(0, centerY - (stage.clientHeight / 2));

        // Avoid tiny jitter re-centers
        if (!force
            && Math.abs(stage.scrollLeft - nextLeft) < 8
            && Math.abs(stage.scrollTop - nextTop) < 8) {
            return;
        }

        stage.scrollLeft = nextLeft;
        stage.scrollTop = nextTop;
        this.setState({
            scrollLeft: nextLeft,
            scrollTop: nextTop,
            stageW: stage.clientWidth,
            stageH: stage.clientHeight
        });
    };

    closeShell = () => {
        if (!this.props.embedded) {
            document.body.style.overflow = '';
        }
        this.unobserveStage();
        if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);
        this.setState({ ready: false });
    };

    observeStage = () => {
        this.unobserveStage();
        const stage = this.stageRef.current;
        if (!stage) return;
        if (typeof ResizeObserver !== 'undefined') {
            this.resizeObserver = new ResizeObserver(() => {
                if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);
                this.resizeRaf = requestAnimationFrame(() => {
                    this.updateLayout({ centerScroll: false });
                });
            });
            this.resizeObserver.observe(stage);
        }
        stage.addEventListener('wheel', this.handleWheel, { passive: false });
    };

    unobserveStage = () => {
        const stage = this.stageRef.current;
        if (stage) {
            stage.removeEventListener('wheel', this.handleWheel);
        }
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
        if (this.resizeRaf) {
            cancelAnimationFrame(this.resizeRaf);
            this.resizeRaf = null;
        }
    };

    handleWheel = (e) => {
        if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            e.stopPropagation();

            const now = Date.now();
            if (this.lastWheelTime && now - this.lastWheelTime < 100) {
                return;
            }
            this.lastWheelTime = now;

            if (e.deltaY < 0) {
                this.handleZoom(1);
            } else if (e.deltaY > 0) {
                this.handleZoom(-1);
            }
        }
    };

    updateLayout = ({ centerScroll = false } = {}) => {
        const stage = this.stageRef.current;
        if (!stage) return;

        const {
            zoom,
            marginLeft: prevMarginLeft = 24,
            marginTop: prevMarginTop = 24
        } = this.state;
        const validZoom = isNaN(zoom) || zoom <= 0 ? 1.0 : zoom;
        const visualW = this.cw * validZoom;
        const visualH = this.ch * validZoom;
        const panPad = this.getPanPad(stage);

        let marginLeft = panPad;
        let marginTop = panPad;
        const marginRight = panPad;
        const marginBottom = panPad;

        if (!this.props.embedded && stage.clientWidth > 0) {
            marginLeft = Math.max(panPad, Math.round((stage.clientWidth - visualW) / 2));
        }
        if (!this.props.embedded && stage.clientHeight > 0) {
            marginTop = Math.max(panPad, Math.round((stage.clientHeight - visualH) / 2));
        }

        const deltaX = marginLeft - prevMarginLeft;
        const deltaY = marginTop - prevMarginTop;

        const marginChanged =
            Math.abs(deltaX) >= 2
            || Math.abs(deltaY) >= 2;

        if (marginChanged && !centerScroll) {
            stage.scrollLeft = Math.max(0, stage.scrollLeft + deltaX);
            stage.scrollTop = Math.max(0, stage.scrollTop + deltaY);
        }

        const applyScroll = () => {
            if (!centerScroll || !this.stageRef.current) return;
            const el = this.stageRef.current;
            el.scrollLeft = Math.max(0, (el.scrollWidth - el.clientWidth) / 2);
            el.scrollTop = Math.max(0, (el.scrollHeight - el.clientHeight) / 2);
        };

        this.setState({
            marginLeft,
            marginTop,
            marginRight,
            marginBottom,
            scrollLeft: stage.scrollLeft,
            scrollTop: stage.scrollTop,
            stageW: stage.clientWidth,
            stageH: stage.clientHeight
        }, applyScroll);
    };

    handleStageScroll = () => {
        if (!this.stageRef.current) return;
        if (this.scrollRaf) cancelAnimationFrame(this.scrollRaf);
        this.scrollRaf = requestAnimationFrame(() => {
            const stage = this.stageRef.current;
            if (!stage) return;
            this.setState({
                scrollLeft: stage.scrollLeft,
                scrollTop: stage.scrollTop,
                stageW: stage.clientWidth,
                stageH: stage.clientHeight
            });
        });
    };

    handleMiniMapPan = (pctX, pctY) => {
        const stage = this.stageRef.current;
        if (!stage) return;
        const { zoom, marginLeft = 24, marginTop = 24, marginRight = 24, marginBottom = 24 } = this.state;
        const validZoom = isNaN(zoom) || zoom <= 0 ? 1.0 : zoom;
        const totalW = marginLeft + (this.cw * validZoom) + marginRight;
        const totalH = marginTop + (this.ch * validZoom) + marginBottom;

        const maxScrollX = Math.max(1, totalW - stage.clientWidth);
        const maxScrollY = Math.max(1, totalH - stage.clientHeight);

        stage.scrollLeft = pctX * maxScrollX;
        stage.scrollTop = pctY * maxScrollY;

        this.setState({
            scrollLeft: stage.scrollLeft,
            scrollTop: stage.scrollTop
        });
    };

    handleMouseDown = (e) => {
        if (
            e.target.closest('.qd-data-lineage-zoom')
            || e.target.closest('.lin-zoom')
            || e.target.closest('.lin-legend')
            || e.target.closest('.qd-data-lineage-minimap')
            || e.target.closest('.lin-minimap')
            || e.target.closest('.lg-list__item')
            || e.target.closest('.action-icon')
            || e.target.closest('.lg-panel-title')
            || e.target.closest('.pl-item')
            || e.target.closest('.pl-action')
            || e.target.closest('.pl-card__head')
            || e.target.closest('a')
            || e.target.closest('button')
        ) return;
        this.panActive = true;
        this.panStartX = e.clientX;
        this.panStartY = e.clientY;
        this.panStartScrollLeft = this.stageRef.current ? this.stageRef.current.scrollLeft : 0;
        this.panStartScrollTop = this.stageRef.current ? this.stageRef.current.scrollTop : 0;
        if (this.stageRef.current) this.stageRef.current.classList.add('grabbing');
        window.addEventListener('mousemove', this.handleMouseMove);
        window.addEventListener('mouseup', this.handleMouseUp);
    };

    handleMouseMove = (e) => {
        if (!this.panActive || !this.stageRef.current) return;
        this.stageRef.current.scrollLeft = this.panStartScrollLeft - (e.clientX - this.panStartX);
        this.stageRef.current.scrollTop = this.panStartScrollTop - (e.clientY - this.panStartY);
    };

    handleMouseUp = () => {
        this.panActive = false;
        if (this.stageRef.current) this.stageRef.current.classList.remove('grabbing');
        window.removeEventListener('mousemove', this.handleMouseMove);
        window.removeEventListener('mouseup', this.handleMouseUp);
    };

    handleZoom = (dir) => {
        this.setState((prev) => {
            let nextZoom = prev.zoom;
            const stage = this.stageRef.current;
            if (dir === 'fit') {
                if (stage && stage.clientWidth > 0 && stage.clientHeight > 0) {
                    const fitW = (stage.clientWidth - 96) / this.cw;
                    const fitH = (stage.clientHeight - 72) / this.ch;
                    nextZoom = Math.min(1.2, Math.max(0.15, Math.min(fitW, fitH)));
                } else {
                    nextZoom = 1.0;
                }
            } else {
                const step = typeof dir === 'number' ? dir : 0;
                nextZoom = Math.min(1.6, Math.max(0.15, prev.zoom + step * 0.1));
            }

            if (isNaN(nextZoom) || nextZoom <= 0) {
                nextZoom = 1.0;
            }
            return { zoom: nextZoom };
        }, () => {
            this.updateLayout({ centerScroll: true });
        });
    };

    render() {
        if (!this.props.show) return null;

        const { zoom, marginLeft, marginTop, marginRight = 24, marginBottom = 24, scrollLeft, scrollTop, stageW, stageH, theme, ready } = this.state;
        const {
            onClose,
            title,
            stats,
            headerExtra = null,
            legend,
            sidePanel = null,
            cards = [],
            posMap = {},
            children,
            embedded = false,
            hideHeader = false,
            focusId = null
        } = this.props;

        const validZoom = isNaN(zoom) || zoom <= 0 ? 1.0 : zoom;
        const visualW = this.cw * validZoom;
        const visualH = this.ch * validZoom;
        const showHeader = !hideHeader && !embedded;

        return (
            <div
                className={`qd-data-lineage-overlay lin-overlay on${embedded ? ' lin-overlay--embedded' : ''}`}
                id={embedded ? 'linOverlayEmbedded' : 'linOverlay'}
                style={embedded ? { height: this.props.height || '100%' } : undefined}
            >
                <div className={`qd-data-lineage-modal lin-modal ${theme === 'dark' ? 'dark-theme' : ''}`}>
                    {showHeader && (
                        <div className="qd-data-lineage-header lin-head">
                            <div>
                                <div className="qd-data-lineage-header__title lin-h-title">{title}</div>
                                {stats}
                            </div>
                            <div className="qd-data-lineage-header__actions lin-h-r">
                                {headerExtra}
                                <span className="qd-data-lineage-header__icon lin-hicon"></span>
                                {typeof onClose === 'function' && (
                                    <button className="qd-data-lineage-header__close lin-close" onClick={onClose}><X size={18} /></button>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="qd-data-lineage-viewport lin-viewport">
                        <div className="lin-stage-wrap">
                            <div
                                className="lin-stage"
                                id={embedded ? 'linStageEmbedded' : 'linStage'}
                                ref={this.stageRef}
                                onMouseDown={this.handleMouseDown}
                                onScroll={this.handleStageScroll}
                            >
                                <div
                                    className="lin-canvas-shell"
                                    style={{
                                        width: `${visualW}px`,
                                        height: `${visualH}px`,
                                        marginLeft: `${marginLeft}px`,
                                        marginTop: `${marginTop}px`,
                                        marginRight: `${marginRight}px`,
                                        marginBottom: `${marginBottom}px`,
                                        opacity: ready ? 1 : 0,
                                        transition: 'opacity 0.15s ease'
                                    }}
                                >
                                    <div
                                        className="lin-canvas"
                                        id={embedded ? 'linCanvasEmbedded' : 'linCanvas'}
                                        style={{
                                            width: `${this.cw}px`,
                                            height: `${this.ch}px`,
                                            transform: `scale(${validZoom})`,
                                            transformOrigin: '0 0'
                                        }}
                                    >
                                        {children}
                                    </div>
                                </div>
                            </div>

                            <MiniMap
                                cards={cards}
                                posMap={posMap}
                                canvasW={this.cw}
                                canvasH={this.ch}
                                scrollLeft={scrollLeft}
                                scrollTop={scrollTop}
                                stageW={stageW}
                                stageH={stageH}
                                zoom={validZoom}
                                marginLeft={marginLeft}
                                marginTop={marginTop}
                                focusId={focusId}
                                onPanTo={this.handleMiniMapPan}
                            />

                            <div className="lin-zoom">
                                <button type="button" onClick={() => this.handleZoom('fit')} title="Fit to screen"><Scan size={17} /></button>
                                <button type="button" onClick={() => this.handleZoom(1)}><Plus size={17} /></button>
                                <span className="pct">{Math.round(validZoom * 100)}%</span>
                                <button type="button" onClick={() => this.handleZoom(-1)}><Minus size={17} /></button>
                            </div>

                            {legend}
                        </div>

                        {sidePanel}
                    </div>
                </div>
            </div>
        );
    }
}

